/**
 * True when titleImageId matches the legacy guess from old <adventure:slug> parsing:
 * `${slug}:${slug.replace(/-/g, '_')}`. That often does not exist in registry.json.
 */
export function isSyntheticAdventureMarkupTitleImageId(adventureId: string, titleImageId: string): boolean {
  const colon = titleImageId.indexOf(':');
  if (colon === -1) {
    return false;
  }
  const nsAdventure = titleImageId.slice(0, colon);
  const imageKey = titleImageId.slice(colon + 1);
  return nsAdventure === adventureId && imageKey === adventureId.replace(/-/g, '_');
}

/**
 * Resolve a title image id for display using the adventure header and the image server's /title-images list.
 * When the header id is missing or a known-bad synthetic, pick the first registered id for this adventure.
 */
export function resolveAdventureTitleImageId(
  adventureId: string,
  headerTitleImageId: string | undefined,
  availableIds: readonly string[]
): string {
  const raw = (headerTitleImageId ?? '').trim();
  if (!raw || raw === 'generic') {
    return 'generic';
  }

  if (raw.includes(':')) {
    return raw;
  }

  const listLoaded = availableIds.length > 0;
  const avail = listLoaded ? new Set(availableIds) : null;
  const namespaced = `${adventureId}:${raw}`;

  // Prefer central registry entry when header uses a shared image id (e.g. dragon-slayer → fantasy)
  if (avail?.has(raw)) {
    return raw;
  }

  if (avail?.has(namespaced)) {
    return namespaced;
  }

  if (avail) {
    const prefixed = availableIds.filter((id) => id.startsWith(`${adventureId}:`)).sort();
    const firstPrefixed = prefixed[0];
    if (firstPrefixed !== undefined) {
      if (isSyntheticAdventureMarkupTitleImageId(adventureId, raw) || !avail.has(raw)) {
        return firstPrefixed;
      }
    }
    if (isSyntheticAdventureMarkupTitleImageId(adventureId, raw)) {
      return 'generic';
    }
  }

  // Adventure-local id: namespace by convention (works even before /title-images list loads)
  if (adventureId) {
    return namespaced;
  }

  return raw;
}

/** Banner IDs use the same namespacing rules as title images. */
export const resolveAdventureBannerId = resolveAdventureTitleImageId;
